import { useCallback, useEffect, useState } from "react";
import { Check, AlertTriangle, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { ReceiptCard, ReceiptSummary, ConfBadge } from "./ReceiptCard";
import { ReceiptUploadButtons } from "./ReceiptUpload";
import { api, errMsg } from "../lib/api";
import { fmtEUR } from "../lib/format";

export const CoverageLine = ({ cov }) => {
  if (!cov || cov.state === "keine") return null;
  const map = {
    voll: ["bg-emerald-50 text-emerald-800 border-emerald-200", Check, ""],
    teilweise: ["bg-amber-50 text-amber-900 border-amber-200", AlertTriangle, " – teilweise gedeckt"],
    ueber: ["bg-blue-50 text-blue-800 border-blue-200", Info, " – Belege höher als Buchung"],
    unbekannt: ["bg-slate-50 text-slate-700 border-slate-200", Info, ""],
  };
  const [cls, Icon, suffix] = map[cov.state] || map.unbekannt;
  return (
    <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium ${cls}`} data-testid="coverage-line" data-state={cov.state}>
      <Icon className="h-4 w-4 shrink-0" />
      {cov.state === "unbekannt" ? "Deckung nicht berechenbar (Fremdwährung/Betrag fehlt)" : <span>Belege decken <span className="font-num">{fmtEUR(cov.covered)}</span> von <span className="font-num">{fmtEUR(cov.total)}</span>{suffix}</span>}
    </div>
  );
};

export const ReceiptsSection = ({ tx, onChanged, onConfirmed }) => {
  const [data, setData] = useState({ receipts: [], coverage: null });
  const [inbox, setInbox] = useState([]);
  const load = useCallback(async () => {
    const [d, s] = await Promise.all([api.txReceipts(tx.id), api.txInboxSuggestions(tx.id)]);
    setData(d); setInbox(s);
  }, [tx.id]);
  useEffect(() => { load(); }, [load, tx.status]);
  const reading = data.receipts.some((r) => r.ai_status === "pending" || r.ai_status === "running");
  useEffect(() => {
    if (!reading) return;
    const t = setInterval(load, 2500);
    return () => clearInterval(t);
  }, [reading, load]);
  const changed = () => { load(); onChanged(); };
  const confirm = async (rid) => {
    try { await api.linkReceipt(rid, tx.id); toast.success("Beleg zugeordnet – Beleg vorhanden"); load(); onConfirmed(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid gap-3" data-testid="receipts-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-base font-bold text-slate-900">Belege <span className="font-num text-slate-400">({data.receipts.length})</span></h3>
        <ReceiptUploadButtons transactionId={tx.id} onDone={changed} testPrefix="tx-receipt" />
      </div>
      <CoverageLine cov={data.coverage} />
      {data.receipts.map((rc) => <ReceiptCard key={rc.id} rc={rc} compact contextTxId={tx.id} onChanged={changed} />)}
      {data.receipts.length === 0 && <div className="rounded-lg border border-dashed border-slate-300 p-3 text-sm text-slate-500" data-testid="no-receipts">Noch kein Beleg verknüpft.</div>}
      {inbox.length > 0 && (
        <div className="grid gap-2" data-testid="inbox-suggestions">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Passende Belege aus dem Eingang</div>
          {inbox.map(({ receipt, suggestion }) => (
            <div key={receipt.id} className="grid gap-2 rounded-lg border border-amber-200 bg-amber-50/50 p-3" data-testid={`inbox-suggestion-${receipt.id}`}>
              <div className="flex items-start gap-2"><ConfBadge c={suggestion.confidence} /><ReceiptSummary rc={receipt} /></div>
              <div className="text-xs text-slate-600">{suggestion.reason}</div>
              <Button size="sm" onClick={() => confirm(receipt.id)} data-testid={`confirm-inbox-suggestion-${receipt.id}`} className="w-fit gap-1 bg-slate-900 hover:bg-slate-800"><Check className="h-3.5 w-3.5" />Bestätigen</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
